<template>
  <v-container fluid style="margin-top: 2%">
    <v-dialog v-model="confirm.open" persistent max-width="520" style="border-radius: 10px">
      <v-card>
        <v-card-title class="headline">
          <span style="color: #d60000">{{ confirm.title }}</span>
        </v-card-title>
        <v-card-text>
          <span style="white-space: pre-line">{{ confirm.message }}</span>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="resolveConfirm(false)">{{ $t('accessPermissions.actions.cancel') }}</v-btn>
          <v-btn color="red accent-4" class="white--text" @click="resolveConfirm(true)">
            {{ confirm.confirmText }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-menu
      v-model="menuEfeito.open"
      :position-x="menuEfeito.x"
      :position-y="menuEfeito.y"
      absolute
      offset-y
    >
      <v-list dense>
        <v-subheader>{{ menuEfeito.titulo }}</v-subheader>
        <v-list-item
          v-for="opcao in opcoesEfeito"
          :key="opcao.value"
          @click="definirEfeitoUsuario(menuEfeito.id, opcao.value)"
        >
          <v-list-item-icon class="mr-3">
            <v-icon :color="opcao.color">{{ opcao.icon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ opcao.label }}</v-list-item-title>
            <v-list-item-subtitle>{{ opcao.hint }}</v-list-item-subtitle>
          </v-list-item-content>
          <v-list-item-action v-if="efeitoLocalUsuario(menuEfeito.id) === opcao.value">
            <v-icon small color="primary">mdi-check</v-icon>
          </v-list-item-action>
        </v-list-item>
      </v-list>
    </v-menu>

    <v-card class="elevation-7" style="border-radius: 10px">
      <v-card-title class="pb-0">
        <span class="headline" style="color: #4a4a4a">{{ $t('accessPermissions.title') }}</span>
        <v-spacer />
        <v-btn
          icon
          :loading="loadingCatalogo"
          :title="$t('accessPermissions.actions.reloadCatalog')"
          @click="carregarCatalogos"
        >
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-subtitle class="pt-2">{{ $t('accessPermissions.subtitle') }}</v-card-subtitle>

      <v-tabs v-model="tab" show-arrows>
        <v-tab>
          <v-icon left small>mdi-account-group</v-icon>{{ $t('accessPermissions.tabs.profiles') }}
          <v-icon v-if="perfilDirty" x-small color="orange" class="ml-1">mdi-circle</v-icon>
        </v-tab>
        <v-tab>
          <v-icon left small>mdi-account</v-icon>{{ $t('accessPermissions.tabs.users') }}
          <v-icon v-if="usuarioDirty" x-small color="orange" class="ml-1">mdi-circle</v-icon>
        </v-tab>
        <v-tab>
          <v-icon left small>mdi-link-variant</v-icon>{{ $t('accessPermissions.tabs.screens') }}
          <v-icon v-if="telaDirty" x-small color="orange" class="ml-1">mdi-circle</v-icon>
        </v-tab>
        <v-tab>
          <v-icon left small>mdi-earth</v-icon>{{ $t('accessPermissions.tabs.defaults') }}
          <v-icon v-if="padraoDirty" x-small color="orange" class="ml-1">mdi-circle</v-icon>
        </v-tab>
      </v-tabs>
      <v-divider />

      <v-tabs-items v-model="tab">
        <!-- PERFIS -->
        <v-tab-item>
          <v-card-text>
            <v-row dense align="center">
              <v-col cols="12" md="5">
                <v-autocomplete
                  v-model="perfilSelect"
                  :items="perfisItems"
                  item-text="nome"
                  item-value="id_perfil_usuario"
                  :label="$t('accessPermissions.profiles.select')"
                  :loading="loadingPerfis"
                  :no-data-text="$t('accessPermissions.profiles.none')"
                  prepend-inner-icon="mdi-account-group"
                  outlined
                  dense
                  hide-details
                  @change="selecionarPerfil"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="perfil.busca"
                  :label="$t('accessPermissions.screens.search')"
                  prepend-inner-icon="mdi-magnify"
                  :disabled="!perfil.id"
                  outlined
                  dense
                  hide-details
                  clearable
                />
              </v-col>
              <v-col cols="12" md="3" class="text-md-right">
                <v-btn text :disabled="!perfilDirty || perfil.saving" @click="descartarPerfil">
                  {{ $t('accessPermissions.actions.discard') }}
                </v-btn>
                <v-btn
                  color="primary"
                  :disabled="!podeEditar || !perfilDirty"
                  :loading="perfil.saving"
                  @click="salvarPerfil"
                >
                  <v-icon left>mdi-content-save</v-icon>{{ $t('accessPermissions.actions.save') }}
                </v-btn>
              </v-col>
            </v-row>

            <v-alert v-if="!perfil.id" type="info" text dense class="mt-4">
              {{ $t('accessPermissions.profiles.selectHint') }}
            </v-alert>

            <template v-else>
              <div class="text-caption grey--text mt-3 mb-1">{{ $t('accessPermissions.profiles.matrixHint') }}</div>
              <v-data-table
                :headers="matrizHeaders"
                :items="perfil.busca ? filtrarTelas(perfil.busca) : telasLinhas"
                item-key="id_tela"
                dense
                fixed-header
                height="55vh"
                :items-per-page="-1"
                hide-default-footer
                :loading="perfil.loading || loadingCatalogo"
                :loading-text="$t('accessPermissions.loading')"
                :no-data-text="$t('accessPermissions.screens.empty')"
                class="matriz-permissoes elevation-1"
              >
                <template v-slot:item="{ item }">
                  <tr>
                    <td class="coluna-tela">
                      <div class="font-weight-medium">{{ item.nomeExibicao }}</div>
                      <div class="text-caption grey--text">{{ item.rota_front || item.cod_recurso }}</div>
                    </td>
                    <td
                      v-for="cod in TIPOS"
                      :key="cod"
                      class="text-center"
                      :class="{ 'celula-alterada': item.porTipo[cod] && perfilCelulaAlterada(item.porTipo[cod].id_permissao) }"
                    >
                      <div
                        v-if="item.porTipo[cod]"
                        class="d-inline-flex"
                        :title="textoQtdEndpoints(item.porTipo[cod])"
                      >
                        <v-simple-checkbox
                          :value="perfilSet.has(item.porTipo[cod].id_permissao)"
                          color="primary"
                          :ripple="false"
                          @input="toggleTelaPerfil(item, cod, $event)"
                        />
                      </div>
                    </td>
                  </tr>
                </template>
              </v-data-table>

              <div class="subtitle-1 font-weight-medium mt-6">{{ $t('accessPermissions.endpoints.title') }}</div>
              <div class="text-caption grey--text mb-2">{{ $t('accessPermissions.endpoints.profileHint') }}</div>
              <v-row dense>
                <v-col cols="12" md="6">
                  <v-autocomplete
                    v-model="perfil.adicionar"
                    :search-input.sync="perfil.adicionarBusca"
                    :items="endpointsDisponiveis(perfilSet)"
                    :item-text="textoEndpoint"
                    item-value="id_permissao"
                    :label="$t('accessPermissions.endpoints.add')"
                    :no-data-text="$t('accessPermissions.endpoints.noneAvailable')"
                    prepend-inner-icon="mdi-plus"
                    outlined
                    dense
                    hide-details
                    @change="adicionarAvulsoPerfil"
                  >
                    <template v-slot:item="{ item }">
                      <v-list-item-content>
                        <v-list-item-title>{{ item.rota }}</v-list-item-title>
                        <v-list-item-subtitle>{{ item.nome }}</v-list-item-subtitle>
                      </v-list-item-content>
                      <v-list-item-action>
                        <v-chip x-small label dark :color="corTipo(item.cod_tipo_permissao)">
                          {{ tipoLabel(item.cod_tipo_permissao) }}
                        </v-chip>
                      </v-list-item-action>
                    </template>
                  </v-autocomplete>
                </v-col>
              </v-row>
              <div v-if="perfilAvulsos.length === 0" class="text-caption grey--text mt-2">
                {{ $t('accessPermissions.endpoints.noneGranted') }}
              </div>
              <div v-else class="mt-2">
                <v-chip
                  v-for="ep in perfilAvulsos"
                  :key="ep.id_permissao"
                  class="ma-1"
                  small
                  label
                  outlined
                  close
                  @click:close="removerAvulsoPerfil(ep.id_permissao)"
                >
                  <strong class="mr-2" :class="`${corTipo(ep.cod_tipo_permissao)}--text`">
                    {{ tipoLabel(ep.cod_tipo_permissao) }}
                  </strong>
                  {{ ep.rota }}
                </v-chip>
              </div>
            </template>
          </v-card-text>
        </v-tab-item>

        <!-- USUÁRIOS -->
        <v-tab-item>
          <v-card-text>
            <v-row dense align="center">
              <v-col cols="12" md="5">
                <v-autocomplete
                  v-model="usuarioSelect"
                  :items="usuarioItems"
                  :search-input.sync="usuarioBusca"
                  item-text="nome_usuario"
                  item-value="id_usuario"
                  return-object
                  no-filter
                  clearable
                  :loading="loadingUsuarios"
                  :label="$t('accessPermissions.users.select')"
                  :no-data-text="$t('accessPermissions.users.typeToSearch')"
                  prepend-inner-icon="mdi-account-search"
                  outlined
                  dense
                  hide-details
                  @change="selecionarUsuario"
                >
                  <template v-slot:item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title>{{ item.nome_usuario }}</v-list-item-title>
                      <v-list-item-subtitle>{{ item.login }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                </v-autocomplete>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="usuario.busca"
                  :label="$t('accessPermissions.screens.search')"
                  prepend-inner-icon="mdi-magnify"
                  :disabled="!usuario.id"
                  outlined
                  dense
                  hide-details
                  clearable
                />
              </v-col>
              <v-col cols="12" md="3" class="text-md-right">
                <v-btn text :disabled="!usuarioDirty || usuario.saving" @click="descartarUsuario">
                  {{ $t('accessPermissions.actions.discard') }}
                </v-btn>
                <v-btn
                  color="primary"
                  :disabled="!podeEditar || !usuarioDirty"
                  :loading="usuario.saving"
                  @click="salvarUsuario"
                >
                  <v-icon left>mdi-content-save</v-icon>{{ $t('accessPermissions.actions.save') }}
                </v-btn>
              </v-col>
            </v-row>

            <v-alert v-if="!usuario.id" type="info" text dense class="mt-4">
              {{ $t('accessPermissions.users.selectHint') }}
            </v-alert>

            <template v-else>
              <div class="d-flex flex-wrap align-center mt-3">
                <v-chip small label class="mr-2 mb-1">
                  <v-icon left small>mdi-account-group</v-icon>
                  {{ $t('accessPermissions.users.profile') }}:
                  <strong class="ml-1">{{ usuario.perfilNome || '—' }}</strong>
                </v-chip>
                <v-chip
                  v-for="estado in legendaEstados"
                  :key="estado.key"
                  small
                  outlined
                  class="mr-2 mb-1"
                >
                  <v-icon left small :color="estado.color">{{ estado.icon }}</v-icon>{{ estado.label }}
                </v-chip>
              </div>
              <v-alert v-if="usuario.isSuperadmin" type="info" text dense class="mt-2 mb-0">
                {{ $t('accessPermissions.users.superadmin') }}
              </v-alert>
              <v-alert type="error" text dense border="left" class="mt-2 mb-2">
                {{ $t('accessPermissions.users.denyWins') }}
              </v-alert>

              <v-data-table
                :headers="matrizHeaders"
                :items="usuario.busca ? filtrarTelas(usuario.busca) : telasLinhas"
                item-key="id_tela"
                dense
                fixed-header
                height="55vh"
                :items-per-page="-1"
                hide-default-footer
                :loading="usuario.loading || loadingCatalogo"
                :loading-text="$t('accessPermissions.loading')"
                :no-data-text="$t('accessPermissions.screens.empty')"
                class="matriz-permissoes elevation-1"
              >
                <template v-slot:item="{ item }">
                  <tr>
                    <td class="coluna-tela">
                      <div class="font-weight-medium">{{ item.nomeExibicao }}</div>
                      <div class="text-caption grey--text">{{ item.rota_front || item.cod_recurso }}</div>
                    </td>
                    <td
                      v-for="cod in TIPOS"
                      :key="cod"
                      class="text-center"
                      :class="{ 'celula-alterada': item.porTipo[cod] && usuarioCelulaAlterada(item.porTipo[cod].id_permissao) }"
                    >
                      <v-btn
                        v-if="item.porTipo[cod]"
                        icon
                        small
                        :disabled="usuario.loading"
                        :title="tituloCelulaUsuario(item, cod)"
                        @click="abrirMenuEfeito($event, item, cod)"
                      >
                        <v-icon :color="estadoVisual(item.porTipo[cod].id_permissao).color">
                          {{ estadoVisual(item.porTipo[cod].id_permissao).icon }}
                        </v-icon>
                        <span
                          v-if="efeitoLocalUsuario(item.porTipo[cod].id_permissao) !== 'SEGUIR'"
                          class="marcador-individual"
                        />
                      </v-btn>
                    </td>
                  </tr>
                </template>
              </v-data-table>

              <div class="subtitle-1 font-weight-medium mt-6">{{ $t('accessPermissions.endpoints.title') }}</div>
              <div class="text-caption grey--text mb-2">{{ $t('accessPermissions.endpoints.userHint') }}</div>
              <v-row dense>
                <v-col cols="12" md="6">
                  <v-autocomplete
                    v-model="usuario.adicionar"
                    :search-input.sync="usuario.adicionarBusca"
                    :items="endpointsDisponiveis(usuarioConcedidosSet)"
                    :item-text="textoEndpoint"
                    item-value="id_permissao"
                    :label="$t('accessPermissions.endpoints.add')"
                    :no-data-text="$t('accessPermissions.endpoints.noneAvailable')"
                    prepend-inner-icon="mdi-plus"
                    outlined
                    dense
                    hide-details
                    @change="adicionarAvulsoUsuario"
                  >
                    <template v-slot:item="{ item }">
                      <v-list-item-content>
                        <v-list-item-title>{{ item.rota }}</v-list-item-title>
                        <v-list-item-subtitle>{{ item.nome }}</v-list-item-subtitle>
                      </v-list-item-content>
                      <v-list-item-action>
                        <v-chip x-small label dark :color="corTipo(item.cod_tipo_permissao)">
                          {{ tipoLabel(item.cod_tipo_permissao) }}
                        </v-chip>
                      </v-list-item-action>
                    </template>
                  </v-autocomplete>
                </v-col>
              </v-row>
              <div v-if="usuarioAvulsos.length === 0" class="text-caption grey--text mt-2">
                {{ $t('accessPermissions.endpoints.noneGranted') }}
              </div>
              <div v-else class="tabela-scroll mt-2">
                <v-simple-table dense class="elevation-1">
                  <thead>
                    <tr>
                      <th>{{ $t('accessPermissions.endpoints.route') }}</th>
                      <th>{{ $t('accessPermissions.endpoints.type') }}</th>
                      <th>{{ $t('accessPermissions.users.fromProfile') }}</th>
                      <th>{{ $t('accessPermissions.users.effect') }}</th>
                      <th class="text-right">{{ $t('accessPermissions.actions.actions') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="ep in usuarioAvulsos" :key="ep.id_permissao">
                      <td>
                        <div>{{ ep.rota }}</div>
                        <div class="text-caption grey--text">{{ ep.nome }}</div>
                      </td>
                      <td>
                        <v-chip x-small label dark :color="corTipo(ep.cod_tipo_permissao)">
                          {{ tipoLabel(ep.cod_tipo_permissao) }}
                        </v-chip>
                      </td>
                      <td>
                        <v-icon small :color="origemDe(ep.id_permissao).pelo_perfil ? 'blue' : 'grey'">
                          {{ origemDe(ep.id_permissao).pelo_perfil ? 'mdi-check' : 'mdi-minus' }}
                        </v-icon>
                      </td>
                      <td>
                        <v-btn-toggle
                          :value="usuario.concessoes[ep.id_permissao]"
                          mandatory
                          dense
                          @change="definirEfeitoUsuario(ep.id_permissao, $event)"
                        >
                          <v-btn x-small value="PERMITIR">
                            <v-icon x-small left color="green">mdi-check-circle</v-icon>
                            {{ $t('accessPermissions.effects.allow') }}
                          </v-btn>
                          <v-btn x-small value="NEGAR">
                            <v-icon x-small left color="red">mdi-cancel</v-icon>
                            {{ $t('accessPermissions.effects.deny') }}
                          </v-btn>
                        </v-btn-toggle>
                      </td>
                      <td class="text-right">
                        <v-btn icon small :title="$t('accessPermissions.actions.remove')" @click="definirEfeitoUsuario(ep.id_permissao, 'SEGUIR')">
                          <v-icon small color="red">mdi-delete</v-icon>
                        </v-btn>
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </div>
            </template>
          </v-card-text>
        </v-tab-item>

        <!-- TELAS E ENDPOINTS -->
        <v-tab-item>
          <v-card-text>
            <v-row dense align="center">
              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="telaSelect"
                  :items="telasLinhas"
                  :item-text="textoTela"
                  item-value="id_tela"
                  :label="$t('accessPermissions.screens.select')"
                  :loading="loadingCatalogo"
                  :no-data-text="$t('accessPermissions.screens.empty')"
                  prepend-inner-icon="mdi-monitor"
                  outlined
                  dense
                  hide-details
                  @change="selecionarTela"
                >
                  <template v-slot:item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title>{{ item.nomeExibicao }}</v-list-item-title>
                      <v-list-item-subtitle>{{ item.rota_front || item.cod_recurso }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                </v-autocomplete>
              </v-col>
              <v-col cols="12" md="6" class="text-md-right">
                <v-btn text :disabled="!telaDirty || tela.saving" @click="descartarTela">
                  {{ $t('accessPermissions.actions.discard') }}
                </v-btn>
                <v-btn color="primary" :disabled="!podeEditar || !telaDirty" :loading="tela.saving" @click="salvarTela">
                  <v-icon left>mdi-content-save</v-icon>{{ $t('accessPermissions.actions.save') }}
                </v-btn>
              </v-col>
            </v-row>

            <v-alert v-if="!tela.id" type="info" text dense class="mt-4">
              {{ $t('accessPermissions.screens.selectHint') }}
            </v-alert>

            <template v-else>
              <v-alert type="warning" text dense border="left" class="mt-4">
                {{ $t('accessPermissions.screens.immediateWarning') }}
              </v-alert>

              <v-row dense>
                <v-col cols="12" md="6">
                  <v-autocomplete
                    v-model="tela.adicionar"
                    :search-input.sync="tela.adicionarBusca"
                    :items="endpointsDisponiveisTela"
                    :item-text="textoEndpoint"
                    item-value="id_endpoint"
                    :label="$t('accessPermissions.screens.addEndpoint')"
                    :no-data-text="$t('accessPermissions.endpoints.noneAvailable')"
                    prepend-inner-icon="mdi-plus"
                    outlined
                    dense
                    hide-details
                    @change="adicionarEndpointTela"
                  >
                    <template v-slot:item="{ item }">
                      <v-list-item-content>
                        <v-list-item-title>{{ item.rota }}</v-list-item-title>
                        <v-list-item-subtitle>
                          {{ $tc('accessPermissions.screens.inScreens', item.qtd_telas, { count: item.qtd_telas }) }}
                          <span v-if="item.nome"> · {{ item.nome }}</span>
                        </v-list-item-subtitle>
                      </v-list-item-content>
                      <v-list-item-action>
                        <v-chip x-small label dark :color="corTipo(item.cod_tipo_permissao)">
                          {{ tipoLabel(item.cod_tipo_permissao) }}
                        </v-chip>
                      </v-list-item-action>
                    </template>
                  </v-autocomplete>
                </v-col>
              </v-row>

              <div v-if="tela.loading" class="text-center py-8">
                <v-progress-circular indeterminate color="primary" />
              </div>
              <div v-else-if="tela.atual.length === 0" class="text-caption grey--text mt-4">
                {{ $t('accessPermissions.screens.noEndpoints') }}
              </div>
              <v-row v-else dense class="mt-3">
                <v-col v-for="grupo in telaGrupos" :key="grupo.cod" cols="12" md="6" lg="4">
                  <v-card outlined>
                    <v-card-subtitle class="pb-1 d-flex align-center">
                      <v-chip x-small label dark :color="corTipo(grupo.cod)" class="mr-2">
                        {{ tipoLabel(grupo.cod) }}
                      </v-chip>
                      {{ $tc('accessPermissions.screens.endpointsCount', grupo.itens.length, { count: grupo.itens.length }) }}
                    </v-card-subtitle>
                    <v-list dense class="py-0">
                      <v-list-item v-for="ep in grupo.itens" :key="ep.id_endpoint">
                        <v-list-item-content>
                          <v-list-item-title class="text-body-2">
                            {{ ep.rota }}
                            <v-icon v-if="!telaOriginalSet.has(ep.id_endpoint)" x-small color="orange" :title="$t('accessPermissions.screens.unsaved')">mdi-circle</v-icon>
                          </v-list-item-title>
                          <v-list-item-subtitle v-if="ep.nome">{{ ep.nome }}</v-list-item-subtitle>
                        </v-list-item-content>
                        <v-list-item-action>
                          <v-btn icon small :title="$t('accessPermissions.actions.remove')" @click="removerEndpointTela(ep.id_endpoint)">
                            <v-icon small color="red">mdi-delete</v-icon>
                          </v-btn>
                        </v-list-item-action>
                      </v-list-item>
                    </v-list>
                  </v-card>
                </v-col>
              </v-row>
            </template>
          </v-card-text>
        </v-tab-item>

        <!-- PERMISSÕES PADRÃO -->
        <v-tab-item>
          <v-card-text>
            <v-alert type="info" text dense border="left">
              {{ $t('accessPermissions.defaults.hint') }}
            </v-alert>
            <v-row dense align="center">
              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="padrao.adicionar"
                  :search-input.sync="padrao.adicionarBusca"
                  :items="padraoDisponiveis"
                  item-text="texto"
                  item-value="id_permissao"
                  :label="$t('accessPermissions.defaults.add')"
                  :no-data-text="$t('accessPermissions.endpoints.noneAvailable')"
                  :loading="loadingCatalogo"
                  prepend-inner-icon="mdi-plus"
                  outlined
                  dense
                  hide-details
                  @change="adicionarPadrao"
                >
                  <template v-slot:item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title>{{ item.titulo }}</v-list-item-title>
                      <v-list-item-subtitle>{{ item.kindLabel }} · {{ item.subtitulo }}</v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-chip x-small label dark :color="corTipo(item.cod_tipo)">{{ tipoLabel(item.cod_tipo) }}</v-chip>
                    </v-list-item-action>
                  </template>
                </v-autocomplete>
              </v-col>
              <v-col cols="12" md="6" class="text-md-right">
                <v-btn text :disabled="!padraoDirty || padrao.saving" @click="descartarPadrao">
                  {{ $t('accessPermissions.actions.discard') }}
                </v-btn>
                <v-btn color="primary" :disabled="!podeEditar || !padraoDirty" :loading="padrao.saving" @click="salvarPadrao">
                  <v-icon left>mdi-content-save</v-icon>{{ $t('accessPermissions.actions.save') }}
                </v-btn>
              </v-col>
            </v-row>

            <div v-if="padrao.loading || loadingCatalogo" class="text-center py-8">
              <v-progress-circular indeterminate color="primary" />
            </div>
            <div v-else-if="padraoLinhas.length === 0" class="text-caption grey--text mt-4">
              {{ $t('accessPermissions.defaults.empty') }}
            </div>
            <div v-else class="tabela-scroll mt-3">
              <v-simple-table dense class="elevation-1">
                <thead>
                  <tr>
                    <th>{{ $t('accessPermissions.defaults.kind') }}</th>
                    <th>{{ $t('accessPermissions.defaults.permission') }}</th>
                    <th>{{ $t('accessPermissions.endpoints.type') }}</th>
                    <th class="text-right">{{ $t('accessPermissions.actions.actions') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="linha in padraoLinhas" :key="linha.id_permissao">
                    <td>
                      <v-chip x-small label outlined>
                        <v-icon x-small left>{{ linha.kind === 'tela' ? 'mdi-monitor' : linha.kind === 'endpoint' ? 'mdi-api' : 'mdi-help' }}</v-icon>
                        {{ linha.kindLabel }}
                      </v-chip>
                    </td>
                    <td>
                      <div>
                        {{ linha.titulo }}
                        <v-icon v-if="!padraoOriginalSet.has(linha.id_permissao)" x-small color="orange" :title="$t('accessPermissions.screens.unsaved')">mdi-circle</v-icon>
                      </div>
                      <div class="text-caption grey--text">{{ linha.subtitulo }}</div>
                    </td>
                    <td>
                      <v-chip v-if="linha.cod_tipo" x-small label dark :color="corTipo(linha.cod_tipo)">
                        {{ tipoLabel(linha.cod_tipo) }}
                      </v-chip>
                    </td>
                    <td class="text-right">
                      <v-btn icon small :title="$t('accessPermissions.actions.remove')" @click="removerPadrao(linha.id_permissao)">
                        <v-icon small color="red">mdi-delete</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </div>
          </v-card-text>
        </v-tab-item>
      </v-tabs-items>
    </v-card>
  </v-container>
</template>

<script>
import apiController from "@/utils/apiController";
import EventBus from "@/utils/eventBus";
import translateDbField from "@/utils/translateDbField";

const TIPOS = ["READ", "INSERT", "UPDATE", "DELETE", "EXECUTE"];
const CORES_TIPO = {
  READ: "blue",
  INSERT: "green",
  UPDATE: "orange",
  DELETE: "red",
  EXECUTE: "purple",
};

function chaveIds(ids) {
  return [...ids].map(Number).sort((a, b) => a - b).join(",");
}

function chaveConcessoes(mapa) {
  return Object.keys(mapa)
    .map(Number)
    .sort((a, b) => a - b)
    .map((id) => `${id}:${mapa[id]}`)
    .join(",");
}

export default {
  name: "PermissoesAcesso",
  data: () => ({
    TIPOS,
    tab: 0,
    loadingCatalogo: false,
    loadingPerfis: false,
    tipos: [],
    catalogoTelas: [],
    catalogoEndpoints: [],
    perfis: [],
    confirm: { open: false, title: "", message: "", confirmText: "", resolve: null },
    menuEfeito: { open: false, x: 0, y: 0, id: null, titulo: "" },

    perfilSelect: null,
    perfil: {
      id: null,
      original: [],
      atual: [],
      busca: "",
      loading: false,
      saving: false,
      adicionar: null,
      adicionarBusca: "",
    },

    usuarioSelect: null,
    usuarioBusca: "",
    usuarioResultados: [],
    loadingUsuarios: false,
    buscaTimer: null,
    buscaRequestId: 0,
    usuario: {
      id: null,
      dados: null,
      perfilNome: "",
      isSuperadmin: false,
      concessoesOriginal: {},
      concessoes: {},
      origem: {},
      busca: "",
      loading: false,
      saving: false,
      adicionar: null,
      adicionarBusca: "",
    },

    telaSelect: null,
    tela: {
      id: null,
      original: [],
      atual: [],
      linhas: [],
      loading: false,
      saving: false,
      adicionar: null,
      adicionarBusca: "",
    },

    padrao: {
      original: [],
      atual: [],
      loading: false,
      saving: false,
      adicionar: null,
      adicionarBusca: "",
    },
  }),
  computed: {
    // Botões de salvar só para quem tem edição da tela; a API bloqueia de qualquer forma.
    podeEditar() {
      return this.$can("PERMISSOES_ACESSO", "UPDATE");
    },
    matrizHeaders() {
      return [
        { text: this.$t("accessPermissions.screens.screen"), value: "nomeExibicao", sortable: false },
        ...TIPOS.map((cod) => ({
          text: this.tipoLabel(cod),
          value: cod,
          align: "center",
          sortable: false,
          width: 90,
        })),
      ];
    },
    telasLinhas() {
      return this.catalogoTelas
        .map((tela) => {
          const porTipo = {};
          (tela.permissoes || []).forEach((p) => {
            porTipo[p.cod_tipo_permissao] = p;
          });
          return {
            ...tela,
            porTipo,
            nomeExibicao: translateDbField(tela.translation_key, tela.nome) || tela.cod_recurso,
          };
        })
        .sort((a, b) => a.nomeExibicao.localeCompare(b.nomeExibicao));
    },
    permissaoTelaMap() {
      const mapa = new Map();
      this.telasLinhas.forEach((tela) => {
        (tela.permissoes || []).forEach((p) => {
          mapa.set(Number(p.id_permissao), { tela, permissao: p });
        });
      });
      return mapa;
    },
    permissaoEndpointMap() {
      const mapa = new Map();
      this.catalogoEndpoints.forEach((ep) => {
        if (!mapa.has(Number(ep.id_permissao))) mapa.set(Number(ep.id_permissao), ep);
      });
      return mapa;
    },
    endpointMap() {
      const mapa = new Map();
      this.catalogoEndpoints.forEach((ep) => mapa.set(Number(ep.id_endpoint), ep));
      return mapa;
    },
    perfisItems() {
      return this.perfis
        .map((p) => ({
          ...p,
          nome: translateDbField(p.translation_key, p.descricao_tipo_usuario) || String(p.id_perfil_usuario),
        }))
        .sort((a, b) => a.nome.localeCompare(b.nome));
    },
    legendaEstados() {
      return ["perfil", "permitido", "negado", "sem"].map((key) => ({
        key,
        ...this.visualEstado(key),
        label: this.$t(`accessPermissions.states.${key}`),
      }));
    },
    opcoesEfeito() {
      return [
        {
          value: "SEGUIR",
          label: this.$t("accessPermissions.effects.follow"),
          hint: this.$t("accessPermissions.effects.followHint"),
          icon: "mdi-account-group-outline",
          color: "blue",
        },
        {
          value: "PERMITIR",
          label: this.$t("accessPermissions.effects.allow"),
          hint: this.$t("accessPermissions.effects.allowHint"),
          icon: "mdi-check-circle",
          color: "green",
        },
        {
          value: "NEGAR",
          label: this.$t("accessPermissions.effects.deny"),
          hint: this.$t("accessPermissions.effects.denyHint"),
          icon: "mdi-cancel",
          color: "red",
        },
      ];
    },

    // Perfis
    perfilSet() {
      return new Set(this.perfil.atual.map(Number));
    },
    perfilOriginalSet() {
      return new Set(this.perfil.original.map(Number));
    },
    perfilDirty() {
      return !!this.perfil.id && chaveIds(this.perfil.original) !== chaveIds(this.perfil.atual);
    },
    perfilAvulsos() {
      return this.perfil.atual
        .map((id) => this.permissaoEndpointMap.get(Number(id)))
        .filter(Boolean)
        .sort((a, b) => a.rota.localeCompare(b.rota));
    },

    // Usuários
    usuarioItems() {
      const itens = [...this.usuarioResultados];
      if (this.usuarioSelect && !itens.some((u) => u.id_usuario === this.usuarioSelect.id_usuario)) {
        itens.unshift(this.usuarioSelect);
      }
      return itens;
    },
    usuarioDirty() {
      return (
        !!this.usuario.id &&
        chaveConcessoes(this.usuario.concessoesOriginal) !== chaveConcessoes(this.usuario.concessoes)
      );
    },
    usuarioConcedidosSet() {
      return new Set(Object.keys(this.usuario.concessoes).map(Number));
    },
    usuarioAvulsos() {
      return Object.keys(this.usuario.concessoes)
        .map((id) => this.permissaoEndpointMap.get(Number(id)))
        .filter(Boolean)
        .sort((a, b) => a.rota.localeCompare(b.rota));
    },

    // Telas e endpoints
    telaOriginalSet() {
      return new Set(this.tela.original.map(Number));
    },
    telaDirty() {
      return !!this.tela.id && chaveIds(this.tela.original) !== chaveIds(this.tela.atual);
    },
    endpointsDisponiveisTela() {
      const ligados = new Set(this.tela.atual.map(Number));
      return this.catalogoEndpoints.filter((ep) => !ligados.has(Number(ep.id_endpoint)));
    },
    telaGrupos() {
      const linhasMap = new Map(this.tela.linhas.map((l) => [Number(l.id_endpoint), l]));
      const grupos = {};
      this.tela.atual.forEach((id) => {
        const ep = this.endpointMap.get(Number(id)) || linhasMap.get(Number(id));
        if (!ep) return;
        const cod = ep.cod_tipo_permissao || "?";
        if (!grupos[cod]) grupos[cod] = [];
        grupos[cod].push(ep);
      });
      const ordem = [...TIPOS, ...Object.keys(grupos).filter((c) => !TIPOS.includes(c))];
      return ordem
        .filter((cod) => grupos[cod])
        .map((cod) => ({
          cod,
          itens: grupos[cod].sort((a, b) => a.rota.localeCompare(b.rota)),
        }));
    },

    // Padrão
    padraoOriginalSet() {
      return new Set(this.padrao.original.map(Number));
    },
    padraoDirty() {
      return chaveIds(this.padrao.original) !== chaveIds(this.padrao.atual);
    },
    padraoLinhas() {
      const ordemKind = { tela: 0, endpoint: 1, desconhecida: 2 };
      return this.padrao.atual
        .map((id) => this.descreverPermissao(id))
        .sort(
          (a, b) =>
            ordemKind[a.kind] - ordemKind[b.kind] ||
            a.titulo.localeCompare(b.titulo) ||
            TIPOS.indexOf(a.cod_tipo) - TIPOS.indexOf(b.cod_tipo)
        );
    },
    padraoDisponiveis() {
      const atuais = new Set(this.padrao.atual.map(Number));
      const itens = [];
      this.permissaoTelaMap.forEach((_, id) => {
        if (!atuais.has(id)) itens.push(this.descreverPermissao(id));
      });
      this.permissaoEndpointMap.forEach((_, id) => {
        if (!atuais.has(id)) itens.push(this.descreverPermissao(id));
      });
      return itens.map((item) => ({
        ...item,
        texto: `${item.titulo} ${item.subtitulo} ${item.kindLabel} ${this.tipoLabel(item.cod_tipo)}`,
      }));
    },
  },
  watch: {
    usuarioBusca(valor) {
      if (this.usuarioSelect && valor === this.usuarioSelect.nome_usuario) return;
      clearTimeout(this.buscaTimer);
      this.buscaTimer = setTimeout(() => this.buscarUsuarios(valor || ""), 350);
    },
  },
  created() {
    this.carregarCatalogos();
    this.carregarPerfis();
    this.carregarPadrao();
  },
  beforeDestroy() {
    clearTimeout(this.buscaTimer);
  },
  async beforeRouteLeave(to, from, next) {
    if (!this.perfilDirty && !this.usuarioDirty && !this.telaDirty && !this.padraoDirty) {
      next();
      return;
    }
    const ok = await this.pedirConfirmacao(
      this.$t("accessPermissions.confirm.unsavedTitle"),
      this.$t("accessPermissions.confirm.leavePage"),
      this.$t("accessPermissions.confirm.discardAndContinue")
    );
    next(ok);
  },
  methods: {
    // ---------- utilitários ----------
    tipoLabel(cod) {
      if (!cod) return "";
      const chave = `accessPermissions.types.${cod}`;
      if (this.$te(chave)) return this.$t(chave);
      const tipo = this.tipos.find((t) => t.cod_tipo_permissao === cod);
      return (tipo && tipo.descricao) || cod;
    },
    corTipo(cod) {
      return CORES_TIPO[cod] || "grey";
    },
    textoEndpoint(ep) {
      return `${ep.rota} ${ep.nome || ""} ${this.tipoLabel(ep.cod_tipo_permissao)}`;
    },
    textoTela(tela) {
      return `${tela.nomeExibicao} ${tela.rota_front || ""} ${tela.cod_recurso || ""}`;
    },
    textoQtdEndpoints(permissao) {
      const qtd = Number(permissao.qtd_endpoints) || 0;
      return this.$tc("accessPermissions.screens.endpointsCount", qtd, { count: qtd });
    },
    filtrarTelas(busca) {
      const termo = String(busca).trim().toLowerCase();
      if (!termo) return this.telasLinhas;
      return this.telasLinhas.filter((t) =>
        [t.nomeExibicao, t.nome, t.rota_front, t.cod_recurso]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(termo))
      );
    },
    endpointsDisponiveis(concedidos) {
      return this.catalogoEndpoints.filter((ep) => !concedidos.has(Number(ep.id_permissao)));
    },
    descreverPermissao(id) {
      const idNum = Number(id);
      const deTela = this.permissaoTelaMap.get(idNum);
      if (deTela) {
        return {
          id_permissao: idNum,
          kind: "tela",
          kindLabel: this.$t("accessPermissions.defaults.kindScreen"),
          titulo: deTela.tela.nomeExibicao,
          subtitulo: deTela.tela.rota_front || deTela.tela.cod_recurso || "",
          cod_tipo: deTela.permissao.cod_tipo_permissao,
        };
      }
      const ep = this.permissaoEndpointMap.get(idNum);
      if (ep) {
        return {
          id_permissao: idNum,
          kind: "endpoint",
          kindLabel: this.$t("accessPermissions.defaults.kindEndpoint"),
          titulo: ep.rota,
          subtitulo: ep.nome || "",
          cod_tipo: ep.cod_tipo_permissao,
        };
      }
      return {
        id_permissao: idNum,
        kind: "desconhecida",
        kindLabel: this.$t("accessPermissions.defaults.kindUnknown"),
        titulo: `#${idNum}`,
        subtitulo: "",
        cod_tipo: null,
      };
    },
    limparAutocomplete(alvo) {
      this.$nextTick(() => {
        alvo.adicionar = null;
        alvo.adicionarBusca = "";
      });
    },
    pedirConfirmacao(title, message, confirmText) {
      if (this.confirm.resolve) this.confirm.resolve(false);
      return new Promise((resolve) => {
        this.confirm = { open: true, title, message, confirmText, resolve };
      });
    },
    resolveConfirm(valor) {
      const resolve = this.confirm.resolve;
      this.confirm = { ...this.confirm, open: false, resolve: null };
      if (resolve) resolve(valor);
    },
    confirmarDescarte() {
      return this.pedirConfirmacao(
        this.$t("accessPermissions.confirm.unsavedTitle"),
        this.$t("accessPermissions.confirm.unsavedMessage"),
        this.$t("accessPermissions.confirm.discardAndContinue")
      );
    },
    alertaResposta(response) {
      const data = (response && response.data) || {};
      EventBus.$emit("show-alert", {
        mensagem: data.mensagem || this.$t("accessPermissions.saved"),
        tipo: data.erro ? "error" : "success",
      });
      return !data.erro;
    },

    // ---------- carregamento geral ----------
    async carregarCatalogos() {
      this.loadingCatalogo = true;
      try {
        const [tipos, telas, endpoints] = await Promise.all([
          apiController.request(apiController.endpoints.getTipospermissoes),
          apiController.request(apiController.endpoints.getCatalogotelas),
          apiController.request(apiController.endpoints.getCatalogoendpoints),
        ]);
        this.tipos = Array.isArray(tipos.data) ? tipos.data : [];
        this.catalogoTelas = Array.isArray(telas.data) ? telas.data : [];
        this.catalogoEndpoints = Array.isArray(endpoints.data) ? endpoints.data : [];
      } catch (e) {
        // alerta já exibido pelo apiController
      } finally {
        this.loadingCatalogo = false;
      }
    },
    async recarregarCatalogoTelas() {
      try {
        const [telas, endpoints] = await Promise.all([
          apiController.request(apiController.endpoints.getCatalogotelas),
          apiController.request(apiController.endpoints.getCatalogoendpoints),
        ]);
        this.catalogoTelas = Array.isArray(telas.data) ? telas.data : [];
        this.catalogoEndpoints = Array.isArray(endpoints.data) ? endpoints.data : [];
      } catch (e) {
        // alerta já exibido pelo apiController
      }
    },
    async carregarPerfis() {
      this.loadingPerfis = true;
      try {
        const response = await apiController.request(apiController.endpoints.getallPerfisusuarios);
        this.perfis = Array.isArray(response.data) ? response.data : [];
      } catch (e) {
        // alerta já exibido pelo apiController
      } finally {
        this.loadingPerfis = false;
      }
    },

    // ---------- perfis ----------
    async selecionarPerfil(id) {
      if (id === this.perfil.id) return;
      if (this.perfilDirty && !(await this.confirmarDescarte())) {
        this.perfilSelect = this.perfil.id;
        return;
      }
      this.perfil.id = id || null;
      this.perfil.original = [];
      this.perfil.atual = [];
      if (id) await this.carregarConcessoesPerfil();
    },
    async carregarConcessoesPerfil() {
      const id = this.perfil.id;
      this.perfil.loading = true;
      try {
        const response = await apiController.request(apiController.endpoints.getConcessoesperfil, {
          params: { id },
        });
        if (id !== this.perfil.id) return;
        const ids = (Array.isArray(response.data) ? response.data : []).map((r) => Number(r.id_permissao));
        this.perfil.original = [...ids];
        this.perfil.atual = [...ids];
      } catch (e) {
        // alerta já exibido pelo apiController
      } finally {
        this.perfil.loading = false;
      }
    },
    perfilCelulaAlterada(id) {
      return this.perfilSet.has(Number(id)) !== this.perfilOriginalSet.has(Number(id));
    },
    toggleTelaPerfil(tela, cod, marcado) {
      const ids = new Set(this.perfil.atual.map(Number));
      const alvo = Number(tela.porTipo[cod].id_permissao);
      if (marcado) {
        ids.add(alvo);
        // qualquer ação sobre a tela pressupõe enxergá-la
        if (cod !== "READ" && tela.porTipo.READ) ids.add(Number(tela.porTipo.READ.id_permissao));
      } else {
        ids.delete(alvo);
        if (cod === "READ") {
          TIPOS.forEach((c) => {
            if (tela.porTipo[c]) ids.delete(Number(tela.porTipo[c].id_permissao));
          });
        }
      }
      this.perfil.atual = [...ids];
    },
    adicionarAvulsoPerfil(idPermissao) {
      if (idPermissao != null && !this.perfilSet.has(Number(idPermissao))) {
        this.perfil.atual = [...this.perfil.atual, Number(idPermissao)];
      }
      this.limparAutocomplete(this.perfil);
    },
    removerAvulsoPerfil(idPermissao) {
      this.perfil.atual = this.perfil.atual.filter((id) => Number(id) !== Number(idPermissao));
    },
    descartarPerfil() {
      this.perfil.atual = [...this.perfil.original];
    },
    async salvarPerfil() {
      if (!this.perfilDirty) return;
      this.perfil.saving = true;
      try {
        const response = await apiController.request(apiController.endpoints.updateConcessoesperfil, {
          data: { id_perfil_usuario: this.perfil.id, ids_permissao: [...this.perfilSet] },
        });
        if (this.alertaResposta(response)) {
          await this.carregarConcessoesPerfil();
          this.recarregarOrigemUsuario();
        }
      } catch (e) {
        // alerta já exibido pelo apiController
      } finally {
        this.perfil.saving = false;
      }
    },

    // ---------- usuários ----------
    async buscarUsuarios(search) {
      const requestId = ++this.buscaRequestId;
      this.loadingUsuarios = true;
      try {
        const response = await apiController.request(apiController.endpoints.getUsuarioslazy, {
          data: { page: 1, pageSize: 20, search },
          silent: true,
        });
        if (requestId !== this.buscaRequestId) return;
        const data = response.data || {};
        this.usuarioResultados = Array.isArray(data.items) ? data.items : [];
      } catch (e) {
        if (requestId === this.buscaRequestId) this.usuarioResultados = [];
      } finally {
        if (requestId === this.buscaRequestId) this.loadingUsuarios = false;
      }
    },
    async selecionarUsuario(item) {
      const id = item ? item.id_usuario : null;
      if (id === this.usuario.id) return;
      if (this.usuarioDirty && !(await this.confirmarDescarte())) {
        this.usuarioSelect = this.usuario.dados;
        return;
      }
      this.usuario.id = id;
      this.usuario.dados = item || null;
      this.usuario.perfilNome = "";
      this.usuario.isSuperadmin = false;
      this.usuario.concessoesOriginal = {};
      this.usuario.concessoes = {};
      this.usuario.origem = {};
      if (id) await this.carregarUsuario();
    },
    async carregarUsuario() {
      const id = this.usuario.id;
      this.usuario.loading = true;
      try {
        const [dados, concessoes, origem] = await Promise.all([
          apiController.request(apiController.endpoints.getUsuariosporids, {
            data: { id_usuarios: [id] },
            silent: true,
          }).catch(() => ({ data: [] })),
          apiController.request(apiController.endpoints.getConcessoesusuario, { params: { id } }),
          apiController.request(apiController.endpoints.getOrigempermissoesusuario, { params: { id } }),
        ]);
        if (id !== this.usuario.id) return;
        const info = (Array.isArray(dados.data) ? dados.data : [])[0];
        if (info) {
          this.usuario.perfilNome = translateDbField(info.translation_key, info.descricao_tipo_usuario);
          this.usuario.isSuperadmin = Number(info.is_superadmin) === 1;
        }
        const mapa = {};
        (Array.isArray(concessoes.data) ? concessoes.data : []).forEach((c) => {
          mapa[Number(c.id_permissao)] = c.efeito;
        });
        this.usuario.concessoesOriginal = { ...mapa };
        this.usuario.concessoes = { ...mapa };
        this.aplicarOrigem(origem.data);
      } catch (e) {
        // alerta já exibido pelo apiController
      } finally {
        this.usuario.loading = false;
      }
    },
    aplicarOrigem(rows) {
      const mapa = {};
      (Array.isArray(rows) ? rows : []).forEach((r) => {
        mapa[Number(r.id_permissao)] = r;
      });
      this.usuario.origem = mapa;
    },
    async recarregarOrigemUsuario() {
      const id = this.usuario.id;
      if (!id) return;
      try {
        const response = await apiController.request(apiController.endpoints.getOrigempermissoesusuario, {
          params: { id },
          silent: true,
        });
        if (id === this.usuario.id) this.aplicarOrigem(response.data);
      } catch (e) {
        // recarga oportunista; ignora falha
      }
    },
    origemDe(id) {
      return this.usuario.origem[Number(id)] || {};
    },
    efeitoLocalUsuario(id) {
      if (id == null) return "SEGUIR";
      return this.usuario.concessoes[Number(id)] || "SEGUIR";
    },
    estadoUsuario(id) {
      const efeito = this.efeitoLocalUsuario(id);
      if (efeito === "NEGAR") return "negado";
      if (efeito === "PERMITIR") return "permitido";
      return Number(this.origemDe(id).pelo_perfil) === 1 ? "perfil" : "sem";
    },
    visualEstado(estado) {
      switch (estado) {
        case "negado":
          return { icon: "mdi-cancel", color: "red darken-1" };
        case "permitido":
          return { icon: "mdi-check-circle", color: "green" };
        case "perfil":
          return { icon: "mdi-check-circle-outline", color: "blue" };
        default:
          return { icon: "mdi-circle-outline", color: "grey lighten-1" };
      }
    },
    estadoVisual(id) {
      return this.visualEstado(this.estadoUsuario(id));
    },
    usuarioCelulaAlterada(id) {
      const key = Number(id);
      return (this.usuario.concessoes[key] || null) !== (this.usuario.concessoesOriginal[key] || null);
    },
    tituloCelulaUsuario(tela, cod) {
      const id = tela.porTipo[cod].id_permissao;
      const estado = this.estadoUsuario(id);
      let texto = `${tela.nomeExibicao} · ${this.tipoLabel(cod)}: ${this.$t(`accessPermissions.states.${estado}`)}`;
      if (estado === "negado" && Number(this.origemDe(id).pelo_perfil) === 1) {
        texto += ` (${this.$t("accessPermissions.states.deniedOverridesProfile")})`;
      }
      return `${texto}\n${this.textoQtdEndpoints(tela.porTipo[cod])}`;
    },
    abrirMenuEfeito(event, tela, cod) {
      event.preventDefault();
      this.menuEfeito.open = false;
      this.menuEfeito.x = event.clientX;
      this.menuEfeito.y = event.clientY;
      this.menuEfeito.id = Number(tela.porTipo[cod].id_permissao);
      this.menuEfeito.titulo = `${tela.nomeExibicao} · ${this.tipoLabel(cod)}`;
      this.$nextTick(() => {
        this.menuEfeito.open = true;
      });
    },
    definirEfeitoUsuario(id, efeito) {
      if (id == null) return;
      const key = Number(id);
      if (!efeito || efeito === "SEGUIR") {
        this.$delete(this.usuario.concessoes, key);
      } else {
        this.$set(this.usuario.concessoes, key, efeito);
      }
    },
    adicionarAvulsoUsuario(idPermissao) {
      if (idPermissao != null && !this.usuario.concessoes[Number(idPermissao)]) {
        this.$set(this.usuario.concessoes, Number(idPermissao), "PERMITIR");
      }
      this.limparAutocomplete(this.usuario);
    },
    descartarUsuario() {
      this.usuario.concessoes = { ...this.usuario.concessoesOriginal };
    },
    async salvarUsuario() {
      if (!this.usuarioDirty) return;
      this.usuario.saving = true;
      try {
        const concessoes = Object.keys(this.usuario.concessoes).map((id) => ({
          id_permissao: Number(id),
          efeito: this.usuario.concessoes[id],
        }));
        const response = await apiController.request(apiController.endpoints.updateConcessoesusuario, {
          data: { id_usuario: this.usuario.id, concessoes },
        });
        if (this.alertaResposta(response)) {
          await this.carregarUsuario();
        }
      } catch (e) {
        // alerta já exibido pelo apiController
      } finally {
        this.usuario.saving = false;
      }
    },

    // ---------- telas e endpoints ----------
    async selecionarTela(id) {
      if (id === this.tela.id) return;
      if (this.telaDirty && !(await this.confirmarDescarte())) {
        this.telaSelect = this.tela.id;
        return;
      }
      this.tela.id = id || null;
      this.tela.original = [];
      this.tela.atual = [];
      this.tela.linhas = [];
      if (id) await this.carregarEndpointsTela();
    },
    async carregarEndpointsTela() {
      const id = this.tela.id;
      this.tela.loading = true;
      try {
        const response = await apiController.request(apiController.endpoints.getEndpointstela, {
          params: { id },
        });
        if (id !== this.tela.id) return;
        const linhas = Array.isArray(response.data) ? response.data : [];
        const ids = linhas.map((l) => Number(l.id_endpoint));
        this.tela.linhas = linhas;
        this.tela.original = [...ids];
        this.tela.atual = [...ids];
      } catch (e) {
        // alerta já exibido pelo apiController
      } finally {
        this.tela.loading = false;
      }
    },
    adicionarEndpointTela(idEndpoint) {
      if (idEndpoint != null && !this.tela.atual.map(Number).includes(Number(idEndpoint))) {
        this.tela.atual = [...this.tela.atual, Number(idEndpoint)];
      }
      this.limparAutocomplete(this.tela);
    },
    removerEndpointTela(idEndpoint) {
      this.tela.atual = this.tela.atual.filter((id) => Number(id) !== Number(idEndpoint));
    },
    descartarTela() {
      this.tela.atual = [...this.tela.original];
    },
    async salvarTela() {
      if (!this.telaDirty) return;
      this.tela.saving = true;
      try {
        const response = await apiController.request(apiController.endpoints.updateEndpointstela, {
          data: { id_tela: this.tela.id, ids_endpoint: this.tela.atual.map(Number) },
        });
        if (this.alertaResposta(response)) {
          await Promise.all([this.carregarEndpointsTela(), this.recarregarCatalogoTelas()]);
          this.recarregarOrigemUsuario();
        }
      } catch (e) {
        // alerta já exibido pelo apiController
      } finally {
        this.tela.saving = false;
      }
    },

    // ---------- permissões padrão ----------
    async carregarPadrao() {
      this.padrao.loading = true;
      try {
        const response = await apiController.request(apiController.endpoints.getPermissoespadrao);
        const ids = (Array.isArray(response.data) ? response.data : []).map((r) => Number(r.id_permissao));
        this.padrao.original = [...ids];
        this.padrao.atual = [...ids];
      } catch (e) {
        // alerta já exibido pelo apiController
      } finally {
        this.padrao.loading = false;
      }
    },
    adicionarPadrao(idPermissao) {
      if (idPermissao != null && !this.padrao.atual.map(Number).includes(Number(idPermissao))) {
        this.padrao.atual = [...this.padrao.atual, Number(idPermissao)];
      }
      this.limparAutocomplete(this.padrao);
    },
    removerPadrao(idPermissao) {
      this.padrao.atual = this.padrao.atual.filter((id) => Number(id) !== Number(idPermissao));
    },
    descartarPadrao() {
      this.padrao.atual = [...this.padrao.original];
    },
    async salvarPadrao() {
      if (!this.padraoDirty) return;
      const ok = await this.pedirConfirmacao(
        this.$t("accessPermissions.confirm.defaultsTitle"),
        this.$t("accessPermissions.confirm.defaultsMessage"),
        this.$t("accessPermissions.actions.save")
      );
      if (!ok) return;
      this.padrao.saving = true;
      try {
        const response = await apiController.request(apiController.endpoints.updatePermissoespadrao, {
          data: { ids_permissao: this.padrao.atual.map(Number) },
        });
        if (this.alertaResposta(response)) {
          await Promise.all([this.carregarPadrao(), this.recarregarCatalogoTelas()]);
          this.recarregarOrigemUsuario();
        }
      } catch (e) {
        // alerta já exibido pelo apiController
      } finally {
        this.padrao.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.matriz-permissoes .coluna-tela {
  min-width: 220px;
}
.matriz-permissoes .celula-alterada {
  background-color: rgba(255, 152, 0, 0.15);
}
.matriz-permissoes .v-btn {
  position: relative;
}
.marcador-individual {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: #616161;
}
.tabela-scroll {
  overflow-x: auto;
}
</style>
